(() => {
  const namespaceKey = 'GuruShopHeaderInlineSearch';
  const sourceScript = document.currentScript;
  const requestedRootId = sourceScript?.dataset.headerInlineSearchRoot;
  const existingApi = window[namespaceKey];

  if (existingApi) {
    existingApi.initById(requestedRootId);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();

  const buildContext = (searchRoot) => {
    const form = searchRoot.querySelector('form[role="search"]');
    const input = form?.elements?.q;
    const predictive = searchRoot.querySelector('predictive-search');
    const resultsSlot = predictive?.querySelector('[slot="results"]');

    if (!input || !resultsSlot) {
      return null;
    }

    return { searchRoot, input, resultsSlot };
  };

  const clearResults = (context) => {
    context.resultsSlot.replaceChildren();
  };

  const reopenIfNeeded = (context) => {
    if (context.input.value.trim() !== '' && context.resultsSlot.children.length === 0) {
      context.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const initRoot = (sectionRoot) => {
    if (
      !(sectionRoot instanceof HTMLElement)
      || initializedRoots.has(sectionRoot)
      || sectionRoot.dataset.inlineSearchBound === 'true'
    ) {
      return false;
    }

    const contexts = Array.from(sectionRoot.querySelectorAll('.header-search-inline'))
      .map(buildContext)
      .filter(Boolean);

    if (contexts.length === 0) {
      return false;
    }

    const handlePointerDown = (event) => {
      contexts.forEach((context) => {
        if (!context.searchRoot.contains(event.target)) {
          clearResults(context);
        }
      });
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      contexts.forEach((context) => {
        clearResults(context);

        if (document.activeElement === context.input) {
          context.input.blur();
        }
      });
    };

    contexts.forEach((context) => {
      context.handleFocus = () => reopenIfNeeded(context);
      context.input.addEventListener('focus', context.handleFocus);
    });

    document.addEventListener('pointerdown', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    sectionRoot.dataset.inlineSearchBound = 'true';
    initializedRoots.add(sectionRoot);
    cleanupByRoot.set(sectionRoot, () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);

      contexts.forEach((context) => {
        context.input.removeEventListener('focus', context.handleFocus);
      });

      delete sectionRoot.dataset.inlineSearchBound;
    });

    return true;
  };

  const initById = (rootId) => {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  };

  const cleanupRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || !initializedRoots.has(sectionRoot)) return false;

    cleanupByRoot.get(sectionRoot)?.();
    cleanupByRoot.delete(sectionRoot);
    initializedRoots.delete(sectionRoot);
    return true;
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    const markerSelector = 'script[data-header-inline-search-root]';

    if (scope.matches?.(markerSelector)) {
      initById(scope.dataset.headerInlineSearchRoot);
    }

    scope.querySelectorAll(markerSelector).forEach((marker) => {
      initById(marker.dataset.headerInlineSearchRoot);
    });
  };

  window[namespaceKey] = { initById, initWithin, cleanupRoot };

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupRoot(event.target);
  });

  initById(requestedRootId);
})();
